import React, { useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Button, InputGroup, Table } from 'react-bootstrap';
import Select from 'react-select';
import config from "../../config/config";
import axios from "axios";
import { toast } from 'react-toastify';
import { getAuthToken } from "../../utils/token";

const TrainingSchedule = ({ show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [machineOperator, setMachineOperator] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [trainingList, setTrainingList] = useState([]);
  const [selectedTraining, setSelectedTraining] = useState(null);

  // Form fields
  const [trainingDate, setTrainingDate] = useState('');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch Operators
  const fetchOperatorList = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      setLoading(true);
      const response = await axios.get(`${config.API}/supervisor/machineuser`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response?.data?.operators || [];
      setMachineOperator(data);
    } catch (err) {
      console.error("Error fetching operators:", err.message);
      setMachineOperator([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Training List
  const fetchTrainingList = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      setLoading(true);
      const response = await axios.get(`${config.API}/admin/traininglist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response?.data?.trainings || [];
      const options = data.map((item) => ({
        value: item.id,
        label: item.name || item.training_name || "Unnamed"
      }));
      setTrainingList(options);
    } catch (err) {
      console.error("Error fetching training list:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchOperatorList();
      fetchTrainingList();
      // Reset form fields when modal opens
      setSelectedParticipants([]);
      setSelectedTraining(null);
      setTrainingDate('');
      setDuration('');
      setNotes('');
      setSearchTerm('');
    }
  }, [show]);

  // Filter participants based on search
  const filteredParticipants = machineOperator?.filter((item) => {
    const name = item?.name || item?.operator_name || "";
    const section = item?.section || item?.section_name || "";
    const id = item?.id || item?.operator_id || "";
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      section.toLowerCase().includes(searchTerm.toLowerCase()) ||
      id.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleSelectParticipant = (id) => {
    setSelectedParticipants((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleSchedule = async () => {
    const token = getAuthToken();
    if (!token) return;

    const body = {
      category_id: selectedTraining?.value,       
      training_type: selectedTraining?.label,   
      training_date: trainingDate,
      duration: duration,   
      notes: notes,                   
      participants: machineOperator
        .filter(selects => selectedParticipants.includes(selects?.id || selects?.operator_id))
        .map(select => ({
          employee_id: select?.id || select?.operator_id,
          name: select?.name || select?.operator_name,
          section: select?.section || select?.section_name || "Rubber"
        }))
    };

    try {
      setLoading(true);
      const response = await axios.post(
        `${config.API}/supervisor/sheduletraning`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(response?.data?.message);
      console.log("Training schedule response:", response.data);
      handleClose();
    } catch (err) {
      console.error("Error scheduling training:", err.message);
      toast.error("Failed to schedule training");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered className="font_family">
      <Modal.Body className="p-5">
        <strong className="fs-4">Training Schedule</strong>

        {/* Training Type */}
        <Form.Group className="mb-3 mt-4">
          <Row>
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Type</Form.Label>
            </Col>
            <Col md={10}>
              <Select
                options={trainingList}
                value={selectedTraining}
                onChange={setSelectedTraining}
                placeholder="Select Training"
                isClearable
              />
            </Col>
          </Row>
        </Form.Group>

        {/* Date & Duration */}
        <Row className="mb-3">
          <Col md={6}>
            <Row>
              <Col md={4} className="d-flex align-items-center">
                <Form.Label className="mb-0">Date of Training</Form.Label>
              </Col>
              <Col md={8}>
                <Form.Control 
                  type="date" 
                  value={trainingDate} 
                  onChange={(e) => setTrainingDate(e.target.value)} 
                />
              </Col>
            </Row>
          </Col>

          <Col md={6}>
            <Row>
              <Col md={4} className="d-flex align-items-center">
                <Form.Label className="mb-0">Duration (hrs)</Form.Label>
              </Col>
              <Col md={8}>
                <Form.Control 
                  type="number" 
                  placeholder="Enter Duration" 
                  value={duration} 
                  onChange={(e) => setDuration(e.target.value)} 
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Notes */}
        <Form.Group className="mb-3">
          <Row>
            <Col md={2} className="d-flex align-items-start">
              <Form.Label>Notes</Form.Label>
            </Col>
            <Col md={10}>
              <Form.Control 
                as="textarea" 
                rows={3} 
                placeholder="Enter notes or remarks..." 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
              />
            </Col>
          </Row>
        </Form.Group>

        {/* Participants */}
        <Form.Group className="mb-3">
          <Row>
            <Col md={2} className="d-flex align-items-start">
              <Form.Label>Select Participants</Form.Label>
            </Col>
            <Col md={10}>
              <InputGroup className="mb-2">
                <Form.Control
                  placeholder="Search Participants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <Table borderless size="sm" className="mb-0">
                  <tbody>
                    {filteredParticipants.length > 0 ? (
                      filteredParticipants.map((participants, idx) => {
                        const id = participants?.id || participants?.operator_id || `OPR_${idx + 1}`;
                        const name = participants?.name || participants?.operator_name || "Unnamed";
                        const section ="Rubber"
                        return (
                          <tr key={idx} className="align-middle">
                            <td className="text-nowrap">{id}</td>
                            <td className="text-nowrap">{name}</td>
                            <td className="text-nowrap">
                              Section: <strong>{section}</strong>
                            </td>
                            <td className="text-end">
                              <Form.Check
                                type="checkbox"
                                checked={selectedParticipants.includes(id)}
                                onChange={() => handleSelectParticipant(id)}
                              />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          No participants found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="dark" onClick={handleSchedule}>
          Schedule
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TrainingSchedule;
